import { Grid, Typography } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import type { Dispatch, SetStateAction } from 'react';
import { maxTime } from 'utils/roundingRules';

type TranslationFn = (key: string) => string;
type ErrorsMap = Record<string, string | undefined>;

type ExitFormData = {
    ExitEarlyChangePoint: Dayjs;
    ExitEarlyExternalRounding: Dayjs;
    ExitEarlyInternalRounding: Dayjs;
    ExitEarlyExternalGracePeriod: Dayjs;
    ExitEarlyInternalGracePeriod: Dayjs;
    ExitLateChangePoint: Dayjs;
    ExitLateExternalRounding: Dayjs;
    ExitLateInternalRounding: Dayjs;
    ExitLateExternalGracePeriod: Dayjs;
    ExitLateInternalGracePeriod: Dayjs;
};

interface ExitMarkingProps {
    t: TranslationFn;
    loadingGettingRounding?: boolean;
    formData: ExitFormData;
    updateFormData: (field: keyof ExitFormData, value: Dayjs) => void;
    errors: ErrorsMap;
    setErrors: Dispatch<SetStateAction<ErrorsMap>>;
}

const ExitMarking = ({ t, loadingGettingRounding = false, formData, updateFormData, errors, setErrors }: ExitMarkingProps) => {
    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <Typography variant="body1">
                        <b>{t('settings.rounding_rule.table.modals.add.tabs.in_punch.early_punch_rounding')}</b>
                    </Typography>

                    <br />
                    <br />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.change_point')}
                            value={formData.ExitEarlyChangePoint}
                            onChange={(nv) => nv && updateFormData('ExitEarlyChangePoint', nv)}
                            ampm={false}
                            sx={{ mb: 2.5 }}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={loadingGettingRounding}
                        />

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.external_rounding')}
                                    value={formData.ExitEarlyExternalRounding}
                                    onChange={(nv) => nv && updateFormData('ExitEarlyExternalRounding', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    slotProps={{ textField: { fullWidth: true } }}
                                    disabled={loadingGettingRounding}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    disabled={dayjs(new Date().setHours(0, 0, 0, 0)).isSame(formData.ExitEarlyChangePoint) || loadingGettingRounding}
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.internal_rounding')}
                                    value={formData.ExitEarlyInternalRounding}
                                    onChange={(nv) => nv && updateFormData('ExitEarlyInternalRounding', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.external_grace_period')}
                                    value={formData.ExitEarlyExternalGracePeriod}
                                    onChange={(nv) => nv && updateFormData('ExitEarlyExternalGracePeriod', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    maxTime={maxTime(formData.ExitEarlyExternalRounding)}
                                    minutesStep={1}
                                    onError={(error) => setErrors({ ...errors, ExitEarlyExternalGracePeriod: error ?? '' })}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: errors['ExitEarlyExternalGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                        },
                                    }}
                                    disabled={loadingGettingRounding}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    disabled={dayjs(new Date().setHours(0, 0, 0, 0)).isSame(formData.ExitEarlyChangePoint) || loadingGettingRounding}
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.internal_grace_period')}
                                    value={formData.ExitEarlyInternalGracePeriod}
                                    onChange={(nv) => nv && updateFormData('ExitEarlyInternalGracePeriod', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    maxTime={maxTime(formData.ExitEarlyInternalRounding)}
                                    minutesStep={1}
                                    onError={(error) => setErrors({ ...errors, ExitEarlyInternalGracePeriod: error ?? '' })}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: errors['ExitEarlyInternalGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </Grid>

                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <Typography variant="body1">
                        <b>{t('settings.rounding_rule.table.modals.add.tabs.in_punch.late_punch_rounding')}</b>
                    </Typography>

                    <br />
                    <br />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                            label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.change_point')}
                            value={formData.ExitLateChangePoint}
                            onChange={(nv) => nv && updateFormData('ExitLateChangePoint', nv)}
                            ampm={false}
                            sx={{ mb: 2.5 }}
                            timeSteps={{ hours: 1, minutes: 1 }}
                            slotProps={{ textField: { fullWidth: true } }}
                            disabled={loadingGettingRounding}
                        />

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    disabled={dayjs(new Date().setHours(0, 0, 0, 0)).isSame(formData.ExitLateChangePoint) || loadingGettingRounding}
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.internal_rounding')}
                                    value={formData.ExitLateInternalRounding}
                                    onChange={(nv) => nv && updateFormData('ExitLateInternalRounding', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.external_rounding')}
                                    value={formData.ExitLateExternalRounding}
                                    onChange={(nv) => nv && updateFormData('ExitLateExternalRounding', nv)}
                                    ampm={false}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    slotProps={{ textField: { fullWidth: true } }}
                                    disabled={loadingGettingRounding}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    disabled={dayjs(new Date().setHours(0, 0, 0, 0)).isSame(formData.ExitLateChangePoint) || loadingGettingRounding}
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.internal_grace_period')}
                                    value={formData.ExitLateInternalGracePeriod}
                                    onChange={(nv) => nv && updateFormData('ExitLateInternalGracePeriod', nv)}
                                    ampm={false}
                                    maxTime={maxTime(formData.ExitLateInternalRounding)}
                                    minutesStep={1}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    onError={(error) => setErrors({ ...errors, ExitLateInternalGracePeriod: error ?? '' })}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: errors['ExitLateInternalGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                                <TimePicker
                                    label={t('settings.rounding_rule.table.modals.add.tabs.in_punch.external_grace_period')}
                                    value={formData.ExitLateExternalGracePeriod}
                                    onChange={(nv) => nv && updateFormData('ExitLateExternalGracePeriod', nv)}
                                    ampm={false}
                                    maxTime={maxTime(formData.ExitLateExternalRounding)}
                                    minutesStep={1}
                                    timeSteps={{ hours: 1, minutes: 1 }}
                                    onError={(error) => setErrors({ ...errors, ExitLateExternalGracePeriod: error ?? '' })}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: errors['ExitLateExternalGracePeriod'] === 'maxTime' ? t('settings.rounding_rule.table.modals.add.max_time') : '',
                                        },
                                    }}
                                    disabled={loadingGettingRounding}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </Grid>
            </Grid>
        </>
    );
};

export default ExitMarking;
